import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { register as apiRegister } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export default function RegisterPage() {
  const [form, setForm] = React.useState({
    username: '',
    password: '',
    displayName: '',
    invitationCode: '',
  });
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      if (form.password.length < 8) {
        setError('密码至少 8 位');
        return;
      }

      setBusy(true);
      try {
        await apiRegister(form);
        navigate('/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : '注册失败');
      } finally {
        setBusy(false);
      }
    },
    [form, navigate]
  );

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-sm shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mb-2 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-lg font-bold text-primary-foreground">
              MI
            </div>
          </div>
          <CardTitle className="text-2xl">创建账号</CardTitle>
          <CardDescription>需要管理员提供的邀请码才能注册</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username">用户名</Label>
              <Input
                id="username"
                placeholder="字母和数字，2-32 位"
                value={form.username}
                onChange={update('username')}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">显示名称</Label>
              <Input
                id="displayName"
                placeholder="你的姓名或昵称"
                value={form.displayName}
                onChange={update('displayName')}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">密码</Label>
              <Input
                id="password"
                type="password"
                placeholder="至少 8 位"
                value={form.password}
                onChange={update('password')}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invitationCode">
                邀请码
                <span className="ml-1 text-xs text-muted-foreground">
                  （联系管理员获取）
                </span>
              </Label>
              <Input
                id="invitationCode"
                placeholder="输入邀请码"
                value={form.invitationCode}
                onChange={update('invitationCode')}
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? '注册中…' : '注册'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              已有账号？{' '}
              <Link to="/login" className="font-medium text-foreground underline underline-offset-4 hover:text-primary">
                登录
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}